import React, { useState } from 'react';
import { X, Users, Wifi, Tv, Wind, Coffee, Droplet, DoorOpen } from 'lucide-react';
import '../styles/RoomsPage.css';

const RoomsPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  // Room data
  const roomData = {
    name: "Ocean View Room",
    price: 12000,
    currency: "₹",
    phone: "For bookings, please call Sushil Adap on +91 9867292012",
    managedBy: "Sushil Adap",
    occupancy: 2,
    description: "Experience luxury and tranquility in our spacious Ocean View Rooms. Wake up to breathtaking views of the bay, enjoy modern amenities, and relax in comfort. Perfect for couples seeking a romantic getaway or travelers looking for a peaceful retreat.",
    features: [
      "Stunning ocean views from private balcony",
      "King-size bed with premium linens",
      "Spacious bathroom with hot shower",
      "Modern entertainment system",
      "Climate control",
      "Daily housekeeping"
    ],
    amenities: [
      { icon: <Tv className="w-6 h-6" />, name: "Flat Screen TV" },
      { icon: <DoorOpen className="w-6 h-6" />, name: "Private Balcony" },
      { icon: <Droplet className="w-6 h-6" />, name: "Hot Shower" },
      { icon: <Wifi className="w-6 h-6" />, name: "Free WiFi" },
      { icon: <Wind className="w-6 h-6" />, name: "Air Conditioning" },
      { icon: <Coffee className="w-6 h-6" />, name: "Mini Bar" }
    ],
    totalRooms: 6,
    // Replace these paths with your actual image paths
    images: [
      '/images/room1.jpg',
      '/images/room2.jpg',
      '/images/room3.jpg',
      '/images/room4.jpg',
      '/images/room5.jpg',
      '/images/room6.jpg',
      '/images/rooms7.jpeg',
      '/images/rooms8.jpeg',
      '/images/rooms9.jpeg',
      '/images/rooms10.jpeg',
      '/images/rooms11.jpeg',
      '/images/rooms12.jpeg'
    ],
    video: '/images/room-tour.mov'
  };

  const ImageModal = ({ image, onClose }) => (
    <div className="modal-overlay" onClick={onClose}>
      <button className="modal-close-button" onClick={onClose}>
        <X className="w-8 h-8" />
      </button>
      <img 
        src={image} 
        alt="Room view" 
        className="modal-image"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  const VideoModal = ({ video, onClose }) => (
    <div className="modal-overlay" onClick={onClose}>
      <button className="modal-close-button" onClick={onClose}>
        <X className="w-8 h-8" />
      </button>
      <video 
        controls 
        autoPlay
        className="modal-video"
        onClick={(e) => e.stopPropagation()}
      >
        <source src={video} type="video/mp4" />
        <source src={video} type="video/quicktime" />
        Your browser does not support the video tag.
      </video>
    </div>
  );

  return (
    <div className="rooms-page">
      <div className="about-content-wrapper">
        {/* Hero Section */}
        <div className="rooms-hero">
          <div className="rooms-title">
            <h1>Our Rooms</h1>
          </div>
          
          <div className="rooms-description">
            <p>
              Discover comfort and luxury in our Ocean View Rooms. Each room offers stunning views of the bay, 
              modern amenities, and a peaceful retreat for your perfect island getaway.
            </p>
            <div className="divider"></div>
          </div>

          {/* Room Overview Card */}
          <div className="room-overview-card">
          <div className="room-overview-grid">
            {/* Image Section */}
            <div className="room-image-container">
              <img 
                src={roomData.images[0]} 
                alt="Ocean View Room"
                className="room-main-image"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800';
                }}
              />
              <div className="rooms-available-badge">
                {roomData.totalRooms} Rooms Available
              </div>
            </div>

            {/* Info Section */}
            <div className="room-info-container">
              <h2 className="room-name">{roomData.name}</h2>
              
              {/* <div className="room-price-container">
                <span className="room-price">
                  {roomData.currency}{roomData.price.toLocaleString()}
                </span>
                <span className="room-price-period">per night</span>
              </div> */}

              <div className="room-occupancy">
                <Users className="w-5 h-5 room-occupancy-icon" />
                <span className="room-occupancy-text">
                  Maximum Occupancy: {roomData.occupancy} Guests
                </span>
              </div>

              <p className="room-description">{roomData.description}</p>
            </div>
          </div>
        </div>

        {/* Amenities Section */}
        <div className="amenities-section">
          <h3 className="section-title">Room Amenities</h3>
          <div className="amenities-grid">
            {roomData.amenities.map((amenity, index) => (
              <div key={index} className="amenity-card">
                <div className="amenity-icon">{amenity.icon}</div>
                <span className="amenity-name">{amenity.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Features List */}
        <div className="features-list-section">
          <h3 className="section-title">Room Features</h3>
          <div className="features-card">
            <div className="features-list-grid">
              {roomData.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <div className="feature-bullet" />
                  <span className="feature-text">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="gallery-section">
          <h3 className="section-title">Room Gallery</h3>
          <div className="gallery-grid">
            {roomData.images.map((image, index) => (
              <div 
                key={index}
                className="gallery-item"
                onClick={() => setSelectedImage(image)}
              >
                <img 
                  src={image} 
                  alt={`Room view ${index + 1}`}
                  className="gallery-image"
                  onError={(e) => {
                    e.target.src = `https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400&h=300&fit=crop&crop=center&q=80&auto=format&sig=${index}`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Video Tour Section */}
        <div className="video-tour-section">
          <h3 className="video-tour-title">Take a Virtual Tour</h3>
          <p className="video-tour-subtitle">
            Experience our Ocean View Room before you book
          </p>
          <button 
            onClick={() => setShowVideo(true)}
            className="video-tour-button"
          >
            Watch Video Tour
          </button>
        </div>
      </div>
      </div>
      {/* Modals */}
      {selectedImage && (
        <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />
      )}
      {showVideo && (
        <VideoModal video={roomData.video} onClose={() => setShowVideo(false)} />
      )}
    </div>
  );
};

export default RoomsPage;